import { useState } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function GradientGenerator() {
  const [color1, setColor1] = useState("#3b82f6");
  const [color2, setColor2] = useState("#8b5cf6");
  const [angle, setAngle] = useState(90);

  const gradient = `linear-gradient(${angle}deg, ${color1}, ${color2})`;

  return (
    <ToolPageLayout
      title="CSS Gradient Generator – Create Linear Gradients"
      description="Generate custom CSS linear gradients for your web projects with our easy-to-use visual gradient creator."
      h1="Gradient Generator"
      category="Color Tools"
      categoryPath="/color-tools"
      howToUse={[
        "Choose your first and second colors.",
        "Adjust the angle slider to set the gradient direction.",
        "Copy the generated CSS background code."
      ]}
      faq={[
        { question: "How to use this gradient in CSS?", answer: "Copy the provided background property value and paste it into your CSS stylesheet." },
        { question: "Can I customize the angle?", answer: "Yes, use the angle slider to adjust the gradient rotation from 0 to 360 degrees." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="space-y-2"><Label>Color 1</Label><div className="flex gap-2"><Input value={color1} onChange={e => setColor1(e.target.value)} /><input type="color" value={color1} onChange={e => setColor1(e.target.value)} className="w-10 h-10 rounded" /></div></div>
          <div className="space-y-2"><Label>Color 2</Label><div className="flex gap-2"><Input value={color2} onChange={e => setColor2(e.target.value)} /><input type="color" value={color2} onChange={e => setColor2(e.target.value)} className="w-10 h-10 rounded" /></div></div>
          <div className="space-y-2"><Label>Angle ({angle}°)</Label><Input type="range" min="0" max="360" value={angle} onChange={e => setAngle(Number(e.target.value))} /></div>
          <div className="pt-4 space-y-2"><Label>CSS Code</Label><Input value={`background: ${gradient};`} readOnly className="font-mono text-sm bg-slate-50" /></div>
        </div>
        <div className="w-full h-full min-h-[300px] rounded-xl border border-slate-200" style={{ background: gradient }} />
      </div>
    </ToolPageLayout>
  );
}

export function ColorNameFinder() {
  const [hex, setHex] = useState("#ff0000");
  
  // Basic color map for demonstration, in a real app would use a large color dictionary
  const colors: Record<string, string> = {
    "#FF0000": "Red", "#00FF00": "Green", "#0000FF": "Blue", "#FFFF00": "Yellow", "#00FFFF": "Cyan", "#FF00FF": "Magenta", "#000000": "Black", "#FFFFFF": "White", "#808080": "Gray"
  };

  const getNearest = (h: string) => {
    const upp = h.toUpperCase();
    if (colors[upp]) return colors[upp];
    return "Unknown (Custom Color)";
  };

  return (
    <ToolPageLayout
      title="Color Name Finder – Identify Colors by Hex Code"
      description="Find the name of any color based on its Hex code with our easy color name identifier."
      h1="Color Name Finder"
      category="Color Tools"
      categoryPath="/color-tools"
      howToUse={[
        "Enter or pick a Hex color code.",
        "See the identified name of the color instantly."
      ]}
      faq={[
        { question: "Can this find all color names?", answer: "This tool uses a predefined list for common color names. Custom colors may return 'Unknown'." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8 items-center max-w-2xl mx-auto">
        <div className="space-y-4">
          <Label>Hex Color</Label>
          <div className="flex gap-2">
            <Input value={hex} onChange={e => setHex(e.target.value)} />
            <input type="color" value={hex} onChange={e => setHex(e.target.value)} className="w-10 h-10 rounded" />
          </div>
        </div>
        <div className="p-8 border rounded-xl text-center bg-slate-50">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 border" style={{ backgroundColor: hex }} />
          <h3 className="text-2xl font-bold">{getNearest(hex)}</h3>
        </div>
      </div>
    </ToolPageLayout>
  );
}
